/**
 * Minimal helpers mirrored from `@hookform/resolvers` so we never import that
 * package at runtime. Its dist bundle declares `react-hook-form` as a peer
 * dependency only; on Vercel/webpack that peer is not always resolvable from
 * `node_modules/@hookform/resolvers`, which breaks funnel app builds that
 * transitively import `@wtc/ui`'s barrel export.
 *
 * @see https://github.com/react-hook-form/resolvers/blob/master/src/toNestErrors.ts
 */
import { get, set } from "react-hook-form";
import type {
  FieldError,
  FieldErrors,
  FieldValues,
  Ref,
  ResolverOptions,
} from "react-hook-form";

function convertArrayPath(path: string): string {
  return path.replace(/[[\]]/g, "");
}

function isArrayField(names: string[], path: string): boolean {
  const normalizedPath = convertArrayPath(path).replace(
    /[.*+?^${}()|[\]\\]/g,
    "\\$&",
  );
  return names.some((name) =>
    convertArrayPath(name).match(new RegExp(`^${normalizedPath}\\.\\d+`)),
  );
}

function reportFieldError(
  field: Ref,
  fieldPath: string,
  errors: FieldErrors,
): void {
  if (field && "reportValidity" in field) {
    const error = get(errors, fieldPath) as FieldError | undefined;
    field.setCustomValidity((error && error.message) || "");
    field.reportValidity();
  }
}

export function validateFieldsNatively<TFieldValues extends FieldValues>(
  errors: FieldErrors<TFieldValues>,
  options: ResolverOptions<TFieldValues>,
): void {
  for (const fieldPath in options.fields) {
    const field = options.fields[fieldPath];
    if (field?.ref && "reportValidity" in field.ref) {
      reportFieldError(field.ref, fieldPath, errors);
    } else if (field?.refs) {
      field.refs.forEach((ref) => reportFieldError(ref, fieldPath, errors));
    }
  }
}

export function toNestErrors<TFieldValues extends FieldValues>(
  errors: FieldErrors,
  options: ResolverOptions<TFieldValues>,
): FieldErrors<TFieldValues> {
  if (options.shouldUseNativeValidation) {
    validateFieldsNatively(errors as FieldErrors<TFieldValues>, options);
  }

  const nestedErrors: FieldErrors = {};

  for (const path in errors) {
    const field = get(options.fields, path);
    const fieldError = Object.assign(errors[path] || {}, {
      ref: field && field.ref,
    });

    if (isArrayField(options.names || Object.keys(errors), path)) {
      const existingError = Object.assign({}, get(nestedErrors, path));
      set(existingError, "root", fieldError);
      set(nestedErrors, path, existingError);
    } else {
      set(nestedErrors, path, fieldError);
    }
  }

  return nestedErrors as FieldErrors<TFieldValues>;
}
